import React, { useContext, useMemo } from 'react'
import { host } from 'api'
import { HttpStatus } from 'api/httpStatus'
import { TokenStorage } from 'api/tokenStorage'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

import { ErrorResponse } from '../../api/ErrorResponse'

export type ErrorHandler = (error: ResponseError, refreshToken: void) => void

const X_TIME_STAMP = 'x-timestamp'
let isAlreadyFetchingAccessToken = false

axios.interceptors.response.use(
  (value) => value,
  (error: AxiosError) => {
    return new Promise((resolve, reject) => {
      console.log(isAlreadyFetchingAccessToken, 'isAlreadyFetchingAccessToken')
      if (!isAlreadyFetchingAccessToken && isErrorUnAuthorized(error)) {
        isAlreadyFetchingAccessToken = true

        return TokenStorage.getNewToken()
          .then((newToken) => {
            isAlreadyFetchingAccessToken = false
            const config = error.config

            if (config?.headers) {
              config.headers['Authorization'] = `Bearer ${newToken}`
            }

            return new Promise(() => {
              axios
                .request(config!)

                .then((response) => {
                  resolve(response)
                })
                .catch((err) => {
                  console.log('3 error', err)
                  reject(err)
                })
            })
          })
          .catch(() => {
            isAlreadyFetchingAccessToken = false
            reject(error)
          })
      }

      return reject(error)
    })
  }
)

const isErrorUnAuthorized = (error: AxiosError): boolean => {
  return isUnAuthorized(error.response?.status)
}

export const isUnAuthorized = (status: number | undefined): boolean => {
  return status === HttpStatus.UNAUTHORIZED
}

export const getErrorResponse = (error: unknown) => {
  if (error instanceof ResponseError) {
    const response = error?.response?.response as AxiosResponse<ErrorResponse>
    return response?.data || undefined
  }
}

type ResponseType = AxiosError & { properties?: Record<string, string> }

export class ResponseError {
  private readonly timer: NodeJS.Timeout

  constructor(
    public response: ResponseType,
    errorHandler?: ErrorHandler
  ) {
    this.timer = setTimeout(() => {
      if (errorHandler) {
        errorHandler(this)
      }
      console.error(this.response)
    }, 200)
  }

  preventDefault(): void {
    clearTimeout(this.timer)
  }
}

export function instanceOfAxiosError(object: unknown): object is AxiosError {
  return typeof object === 'object' && object !== null && 'isAxiosError' in object
}

export class ApiClient {
  errorHandler?: ErrorHandler

  private get config(): AxiosRequestConfig {
    const token = TokenStorage.getAccessToken()
    let headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
    if (token) {
      headers = Object.assign(headers, { Authorization: `Bearer ${TokenStorage.getAccessToken()}` })
    }
    return {
      baseURL: host,
      headers,
      validateStatus: (status) => {
        return status >= 200 && status < 300
      },
      timeout: 60000,
    }
  }

  async rawFetch(url: string, reqConfig: AxiosRequestConfig): Promise<AxiosResponse> {
    try {
      const response = await axios.request({ url, ...reqConfig })

      if (instanceOfAxiosError(response)) {
        throw new ResponseError(response, this.errorHandler)
      }
      const xTimeStamp: string | undefined = response?.headers?.['x-timestamp']
      if (xTimeStamp) {
        localStorage.setItem(X_TIME_STAMP, xTimeStamp)
      }
      return response
    } catch (err) {
      const error = err as AxiosResponse<AxiosError> | Error
      if (typeof err === 'object' && instanceOfAxiosError(error)) {
        throw new ResponseError(error, this.errorHandler)
      }
      throw err
    }
  }

  async fetchAndParse<T>(url: string, reqConfig: AxiosRequestConfig): Promise<T> {
    const config = { ...this.config, ...reqConfig }
    const response = await this.rawFetch(url, config)
    return response.data as T
  }

  async fetch<T>(url: string, reqConfig: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const config = { ...this.config, ...reqConfig }
    return await this.rawFetch(url, config)
  }

  async get<T>(url: string): Promise<T> {
    return this.fetchAndParse(url, { method: 'GET' })
  }

  async post<T>(url: string, data: unknown): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'POST',
      data,
    })
  }

  async put<T>(url: string, data: unknown): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'PUT',
      data,
    })
  }

  async delete<T>(url: string, data: unknown = {}): Promise<T> {
    return this.fetchAndParse(url, {
      method: 'DELETE',
      data,
    })
  }

  async upload<T>(method: 'POST' | 'PUT' = 'POST', url: string, data: FormData): Promise<T> {
    const token = TokenStorage.getAccessToken()

    let headers = {
      Accept: 'application/json',
      'Content-Type': 'multipart/form-data',
    }
    if (token) {
      headers = Object.assign(headers, { Authorization: `Bearer ${TokenStorage.getAccessToken()}` })
    }
    const response = await axios.request({
      url,
      method,
      baseURL: host,
      headers,
      timeout: 60000,
      data,
    })

    if (instanceOfAxiosError(response)) {
      throw new ResponseError(response, this.errorHandler)
    }
    return response.data as T
  }
}

export const ApiClientContext = React.createContext<ApiClient>(new ApiClient())

export function useApiClient<T>(apiType: new (apiClient: ApiClient) => T): T {
  const apiClient = useContext(ApiClientContext)
  return useMemo(() => new apiType(apiClient), [apiClient, apiType])
}
